import { useState, useCallback } from 'react'
import { Copy, Check } from 'lucide-react'
import type { HeadingItem } from '../../lib/content-utils'
import { extractSectionByIndex } from '../../lib/content-utils'

interface ContentTableOfContentsProps {
  headings: HeadingItem[]
  /** When provided, each TOC item shows a copy icon to copy that section's markdown. */
  markdown?: string
  className?: string
}

/**
 * Renders a table of contents from markdown headings. Each item links to #id for in-page scroll.
 * When markdown is provided, each item has a copy icon that copies that section's markdown to the clipboard.
 */
export function ContentTableOfContents({
  headings,
  markdown = '',
  className = '',
}: ContentTableOfContentsProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const handleCopySection = useCallback(
    async (index: number) => {
      const section = extractSectionByIndex(markdown, index)
      if (!section) return
      try {
        await navigator.clipboard.writeText(section)
        setCopiedIndex(index)
        setTimeout(() => setCopiedIndex(null), 2000)
      } catch {
        setCopiedIndex(null)
      }
    },
    [markdown]
  )

  if (headings.length === 0) return null

  return (
    <nav className={className} aria-label="En este artículo">
      <ul className="space-y-1.5">
        {headings.map(({ level, text, id }, index) => (
          <li
            key={id}
            className="flex items-center gap-1.5 group truncate"
            style={{ paddingLeft: level === 1 ? 0 : level === 2 ? 8 : 16 }}
          >
            <a
              href={`#${id}`}
              className="flex-1 min-w-0 text-sm text-slate-600 hover:text-emerald-600 hover:underline transition-colors truncate"
            >
              {text}
            </a>
            {markdown && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  handleCopySection(index)
                }}
                className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-emerald-600 hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                title="Copiar sección"
                aria-label={`Copiar sección: ${text}`}
              >
                {copiedIndex === index ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden />
                ) : (
                  <Copy className="w-3.5 h-3.5" aria-hidden />
                )}
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}
